import { useQuery } from 'data-fetching-client';
import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import { GET_PUBLIC_CURRENCIES } from '../queries/currencyQueries';
import { isCountryCodeString, isCurrencyCodeString, isValidCurrencyCodeForQueryParam } from '../utils/currencyTypes';
import { getCountryCodeFromLocale } from '../utils/countryCodeUtils';
import { getBrowserLocale } from '../utils/getPreferredLanguage';
import { jsx as _jsx } from "react/jsx-runtime";
export const CURRENCY_PARAM = 'currencyCode';
const CurrencyContext = /*#__PURE__*/createContext(null);
function filterValidCurrencyCodes(codes) {
  if (!codes) return [];
  return codes.filter(isCurrencyCodeString);
}

// Derives `displayedCurrencyCode` from URL query param + RPC response on every
// render rather than syncing to local state. URL stays the single source of
// truth so picker writes go to the URL, and the provider reflects them on the
// next render.
export const CurrencyContextProvider = ({
  children
}) => {
  var _data$currencies2, _data$currencies4, _data$currencies5;
  const location = useLocation();
  const queryCurrency = new URLSearchParams(location.search).get(CURRENCY_PARAM);
  const browserLocale = getBrowserLocale();
  const rawCountryCode = getCountryCodeFromLocale();
  const preferredCountryCode = isCountryCodeString(rawCountryCode) ? rawCountryCode : undefined;

  // GET_PUBLIC_CURRENCIES is the hand-written DFC query that calls
  // getChirpEarlyRequest internally so it dedupes against the pre-React early
  // request fired in publicEarlyRequester.ts. The generated useGetPublicCurrencyV2Query
  // hook would skip that cache and double-fetch.
  const {
    data,
    loading,
    error
  } = useQuery(GET_PUBLIC_CURRENCIES, {
    variables: {
      browserLocale,
      preferredCountryCode
    }
  });
  const availableCurrencyCodes = useMemo(() => {
    var _data$currencies;
    return filterValidCurrencyCodes(data === null || data === void 0 || (_data$currencies = data.currencies) === null || _data$currencies === void 0 ? void 0 : _data$currencies.availableCurrencyCodes);
  }, [data === null || data === void 0 || (_data$currencies2 = data.currencies) === null || _data$currencies2 === void 0 ? void 0 : _data$currencies2.availableCurrencyCodes]);
  const serverDefault = useMemo(() => {
    var _data$currencies3;
    const code = data === null || data === void 0 || (_data$currencies3 = data.currencies) === null || _data$currencies3 === void 0 ? void 0 : _data$currencies3.defaultCurrencyCode;
    return isCurrencyCodeString(code) ? code : 'USD';
  }, [data === null || data === void 0 || (_data$currencies4 = data.currencies) === null || _data$currencies4 === void 0 ? void 0 : _data$currencies4.defaultCurrencyCode]);
  const displayedCurrencyCode = useMemo(() => {
    if (isValidCurrencyCodeForQueryParam(queryCurrency, availableCurrencyCodes)) {
      return queryCurrency;
    }
    return serverDefault;
  }, [queryCurrency, availableCurrencyCodes, serverDefault]);
  const countryCode = data === null || data === void 0 || (_data$currencies5 = data.currencies) === null || _data$currencies5 === void 0 ? void 0 : _data$currencies5.countryCode;
  const value = useMemo(() => ({
    displayedCurrencyCode,
    availableCurrencyCodes,
    countryCode,
    isLoading: loading && !error
  }), [displayedCurrencyCode, availableCurrencyCodes, countryCode, loading, error]);
  return /*#__PURE__*/_jsx(CurrencyContext.Provider, {
    value: value,
    children: children
  });
};
export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('CurrencyContext not found');
  }
  return ctx;
};